// CarbonBridgePolicyArchitecture.tsx
import React from "react";

const codeBlock = (code: string) => (
  <pre
    style={{
      background: "#0f172a",
      color: "#e5e7eb",
      padding: "1rem",
      borderRadius: "0.75rem",
      overflowX: "auto",
      fontSize: "0.85rem",
      lineHeight: 1.5,
    }}
  >
    <code>{code}</code>
  </pre>
);

const CarbonBridgePolicyArchitecture: React.FC = () => {
  return (
    <div
      style={{
        maxWidth: "960px",
        margin: "0 auto",
        padding: "2rem 1.5rem 3rem",
        fontFamily:
          "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: "#0f172a",
      }}
    >
      <header style={{ marginBottom: "2rem" }}>
        <h1
          style={{
            fontSize: "2rem",
            fontWeight: 700,
            marginBottom: "0.5rem",
          }}
        >
          CarbonBridge / CarbonChain
        </h1>
        <p style={{ fontSize: "0.95rem", color: "#4b5563" }}>
          Two-model architecture for extraction & enforcement of carbon credit
          rules, integrated into our existing stack.
        </p>
      </header>

      {/* 1. High-level architecture */}
      <section style={{ marginBottom: "2rem" }}>
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: 700,
            marginBottom: "0.75rem",
          }}
        >
          1. High-level Architecture
        </h2>
        <p style={{ marginBottom: "0.75rem" }}>
          The design introduces two LLM services into our existing
          CarbonBridge / CarbonChain environment:
        </p>
        <ul style={{ marginLeft: "1.25rem", marginBottom: "0.75rem" }}>
          <li>
            <strong>Model A – Extractor:</strong> normalises messy inputs
            (UI/API) into a structured, canonical request.
          </li>
          <li>
            <strong>Model B – Enforcer:</strong> applies carbon-credit policy
            rules and decides{" "}
            <code style={{ background: "#e5e7eb", padding: "0 0.2rem" }}>
              approve | reject | needs_more_data
            </code>
            .
          </li>
        </ul>
        <p style={{ marginBottom: "0.75rem" }}>They sit off-chain and mediate access to on-chain actions.</p>

        {codeBlock(
          `Frontend / Integrations
        ↓
Policy Gateway API
        ↓
[Model A] Extractor LLM  →  normalized_request
        ↓
Backend enrichment (DB + chain facts)
        ↓
[Model B] Enforcer LLM   →  decision (approve / reject / needs_more_data)
        ↓
If approved → Transaction Orchestrator → On-chain contracts
        ↓
Audit log / Explorer`
        )}

        <p style={{ marginTop: "0.75rem" }}>
          This keeps on-chain contracts simple and deterministic, while the
          off-chain policy layer remains flexible and governed.
        </p>
      </section>

      {/* 2. Model roles + prompt contracts */}
      <section style={{ marginBottom: "2rem" }}>
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: 700,
            marginBottom: "0.75rem",
          }}
        >
          2. Model Roles & Prompt Contracts
        </h2>

        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: 600,
            marginBottom: "0.4rem",
            marginTop: "0.5rem",
          }}
        >
          2.1 Model A – Extractor
        </h3>
        <p style={{ marginBottom: "0.5rem" }}>
          <strong>Goal:</strong> convert UI / REST inputs into a clean,
          normalised JSON request.
        </p>
        <p style={{ marginBottom: "0.5rem" }}>
          It identifies the action type (mint, retire, price, bridge) and pulls
          out all relevant fields (device IDs, kWh, emission factors, etc.)
          without inventing new values.
        </p>

        {codeBlock(`System prompt (Extractor, summary version):

"You are an extraction engine for a carbon-credit platform.

Given an input describing a requested action, extract a structured JSON object with these fields:

- action_type: 'create_credit' | 'price_credit' | 'retire_credit' | 'update_energy_data' | 'transfer_or_bridge'
- device_ids: array of strings
- kwh: number or null
- emission_factor_kg_per_kwh: number or null
- region: string or null
- requested_credits: number or null
- price_requested_per_credit: number or null
- methodology: string or null
- device_whitelisted: boolean or null
- already_issued_credits_for_interval: boolean or null
- retirement_target_ids: array of credit IDs or null
- chain_context: { source_chain_id, target_chain_id, token_type } or null
- user_role: string or null

If some data are not present, set them to null. Do not infer numbers that are not explicitly mentioned.

Always respond with a single JSON object and nothing else."`)}

        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: 600,
            marginBottom: "0.4rem",
            marginTop: "1.25rem",
          }}
        >
          2.2 Model B – Enforcer
        </h3>
        <p style={{ marginBottom: "0.5rem" }}>
          <strong>Goal:</strong> enforce our carbon-credit rules on the
          normalised request, plus extra context from DB and chains.
        </p>
        <p style={{ marginBottom: "0.5rem" }}>
          It applies the reasoning chain (creation, validation, pricing,
          retirement, energy↔CO₂ correlation, optional blockchain rules) and
          returns a structured decision.
        </p>

        {codeBlock(`System prompt (Enforcer, summary version):

"You are a Carbon Credit Rule Enforcer AI.

Your job is to analyse proposed actions on a carbon-credit platform and decide whether they comply with the policy:

- 1 credit = exactly 1 tCO₂e
- backed by device-measured renewable energy
- devices must be approved/whitelisted
- no double-counting of energy/credits
- clear trace: Energy → Token/Credit → Retirement → Audit
- pricing must be transparent and benchmark-related
- retirement is final and auditable

Always respond in JSON with:
- decision: 'approve' | 'reject' | 'needs_more_data'
- compliance_score: 0..100
- violated_rules: array of strings
- missing_data: array of strings
- reasoning: short explanation
- normalised_facts: machine-readable summary

Do not output anything outside this JSON object."`)}

      </section>

      {/* 3. Data contracts */}
      <section style={{ marginBottom: "2rem" }}>
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: 700,
            marginBottom: "0.75rem",
          }}
        >
          3. Data Contracts (TypeScript)
        </h2>

        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: 600,
            marginBottom: "0.4rem",
            marginTop: "0.5rem",
          }}
        >
          3.1 Normalized Request (Extractor output)
        </h3>

        {codeBlock(`type ActionType =
  | "create_credit"
  | "price_credit"
  | "retire_credit"
  | "update_energy_data"
  | "transfer_or_bridge";

interface NormalizedRequest {
  action_type: ActionType;
  device_ids: string[];
  kwh: number | null;
  emission_factor_kg_per_kwh: number | null;
  region: string | null;
  requested_credits: number | null;
  price_requested_per_credit: number | null;
  methodology: string | null;
  device_whitelisted: boolean | null;
  already_issued_credits_for_interval: boolean | null;
  retirement_target_ids: string[] | null;
  chain_context:
    | {
        source_chain_id: number | null;
        target_chain_id: number | null;
        token_type: "ERGON" | "CARBON" | "CREDIT_NFT" | null;
      }
    | null;
  user_role: string | null;
  raw_payload: unknown; // original user request for audit
}`)}

        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: 600,
            marginBottom: "0.4rem",
            marginTop: "1.25rem",
          }}
        >
          3.2 Enforcement Response (Enforcer output)
        </h3>

        {codeBlock(`type Decision = "approve" | "reject" | "needs_more_data";

interface EnforcementResponse {
  decision: Decision;
  compliance_score: number; // 0..100
  violated_rules: string[];
  missing_data: string[];
  reasoning: string;
  normalised_facts: {
    action_type: ActionType | null;
    device_ids: string[] | null;
    kwh: number | null;
    emission_factor_kg_per_kwh: number | null;
    region: string | null;
    requested_credits: number | null;
    calculated_co2e_tons: number | null;
    expected_credits_from_energy: number | null;
    methodology: string | null;
    pricing_basis: string | null;
    retirement_requested_for: string[] | null;
  };
}`)}
      </section>

      {/* 4. Orchestration flows */}
      <section style={{ marginBottom: "2rem" }}>
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: 700,
            marginBottom: "0.75rem",
          }}
        >
          4. Orchestration Flows
        </h2>

        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: 600,
            marginBottom: "0.4rem",
            marginTop: "0.5rem",
          }}
        >
          4.1 Minting / Credit Creation
        </h3>
        <ol style={{ marginLeft: "1.25rem", marginBottom: "0.75rem" }}>
          <li>
            Frontend calls{" "}
            <code style={{ background: "#e5e7eb", padding: "0 0.2rem" }}>
              POST /policy/check/mint
            </code>{" "}
            with kWh, devices, emission factor, requested credits, etc.
          </li>
          <li>
            API calls Model A (Extractor) → gets{" "}
            <code style={{ background: "#e5e7eb", padding: "0 0.2rem" }}>
              NormalizedRequest
            </code>
            .
          </li>
          <li>
            Backend enriches with DB & chain facts (whitelisted devices,
            intervals already used, region, emission factor).
          </li>
          <li>
            Enriched object goes to Model B (Enforcer) → returns{" "}
            <code style={{ background: "#e5e7eb", padding: "0 0.2rem" }}>
              EnforcementResponse
            </code>
            .
          </li>
          <li>
            If <code>decision = "approve"</code> and score ≥ threshold → call
            Transaction Orchestrator to freeze tokens and mint on-chain.
          </li>
          <li>
            Log request + decision + tx hash in audit DB / explorer.
          </li>
        </ol>

        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: 600,
            marginBottom: "0.4rem",
            marginTop: "1.25rem",
          }}
        >
          4.2 Retirement Flow
        </h3>
        <p style={{ marginBottom: "0.5rem" }}>
          Similar pattern, but the payload contains a list of credit IDs to
          retire. The Enforcer checks that:
        </p>
        <ul style={{ marginLeft: "1.25rem", marginBottom: "0.75rem" }}>
          <li>credits exist and are not already retired,</li>
          <li>retirement is final in your design,</li>
          <li>the user is allowed to retire them.</li>
        </ul>

        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: 600,
            marginBottom: "0.4rem",
            marginTop: "1.25rem",
          }}
        >
          4.3 Pricing Flow
        </h3>
        <p style={{ marginBottom: "0.5rem" }}>
          For pricing, the Enforcer validates that the proposed price per credit
          is:
        </p>
        <ul style={{ marginLeft: "1.25rem", marginBottom: "0.75rem" }}>
          <li>linked to 1 tCO₂e per credit,</li>
          <li>consistent with external benchmarks (EU ETS, ACCU, etc.),</li>
          <li>transparent about quality/time/region modifiers.</li>
        </ul>

        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: 600,
            marginBottom: "0.4rem",
            marginTop: "1.25rem",
          }}
        >
          4.4 Bridge / Cross-chain Flow
        </h3>
        <p style={{ marginBottom: "0.5rem" }}>
          For bridging ERGON/CARBON/credit NFTs:
        </p>
        <ul style={{ marginLeft: "1.25rem", marginBottom: "0.75rem" }}>
          <li>
            Extractor identifies <code>action_type = "transfer_or_bridge"</code>
            .
          </li>
          <li>
            Backend adds chain context, verifies ZK proofs (if any), and checks
            balances/ownership.
          </li>
          <li>
            Enforcer ensures that locking on source + minting on target does not
            allow double-claim on the same climate benefit.
          </li>
        </ul>
      </section>

      {/* 5. Integration into stack */}
      <section style={{ marginBottom: "2rem" }}>
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: 700,
            marginBottom: "0.75rem",
          }}
        >
          5. Integration into Our Stack
        </h2>
        <p style={{ marginBottom: "0.5rem" }}>
          On the backend (FastAPI / Node), introduce a{" "}
          <strong>Policy Gateway</strong> module with endpoints like:
        </p>
        <ul style={{ marginLeft: "1.25rem", marginBottom: "0.75rem" }}>
          <li>
            <code style={{ background: "#e5e7eb", padding: "0 0.2rem" }}>
              POST /policy/check/mint
            </code>
          </li>
          <li>
            <code style={{ background: "#e5e7eb", padding: "0 0.2rem" }}>
              POST /policy/check/retire
            </code>
          </li>
          <li>
            <code style={{ background: "#e5e7eb", padding: "0 0.2rem" }}>
              POST /policy/check/price
            </code>
          </li>
          <li>
            <code style={{ background: "#e5e7eb", padding: "0 0.2rem" }}>
              POST /policy/check/bridge
            </code>
          </li>
        </ul>
        <p style={{ marginBottom: "0.5rem" }}>
          Each endpoint orchestrates Extractor → enrichment → Enforcer, then
          either:
        </p>
        <ul style={{ marginLeft: "1.25rem", marginBottom: "0.75rem" }}>
          <li>returns reasons & missing fields, or</li>
          <li>
            forwards to a separate <code>/execute/*</code> endpoint for
            on-chain execution.
          </li>
        </ul>

        <p style={{ marginBottom: "0.75rem" }}>
          You can also store everything in a{" "}
          <code style={{ background: "#e5e7eb", padding: "0 0.2rem" }}>
            policy_decisions
          </code>{" "}
          table (JSONB fields are perfect) for full traceability for councils
          and auditors.
        </p>
      </section>
    </div>
  );
};

export default CarbonBridgePolicyArchitecture;
